import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import { US_STATE_CODES } from "./intake-sanity"
import { createRequiredPersonNameSchema } from "./person-name"

export const ADDRESS_STREET_1_MAX_LENGTH = 255
export const ADDRESS_STREET_2_MAX_LENGTH = 100
export const ADDRESS_CITY_MAX_LENGTH = 120
export const ADDRESS_REGION_MAX_LENGTH = 120
export const ADDRESS_POSTAL_CODE_MAX_LENGTH = 20
export const PATIENT_SEARCH_MAX_LENGTH = 200
export const US_ZIP_CODE_PATTERN = /^\d{5}(?:-\d{4})?$/

const RequiredAddressStreetSchema = z.string().trim().min(1).apply(utf16Length("max", ADDRESS_STREET_1_MAX_LENGTH))
const OptionalAddressStreetSchema = z.string().trim().apply(utf16Length("max", ADDRESS_STREET_2_MAX_LENGTH))
const AddressCityInputSchema = z.string().trim().min(1).apply(utf16Length("max", ADDRESS_CITY_MAX_LENGTH))
const AddressRegionInputSchema = z.string().trim().min(1).apply(utf16Length("max", ADDRESS_REGION_MAX_LENGTH))
const AddressPostalCodeInputSchema = z.string().trim().min(1).apply(utf16Length("max", ADDRESS_POSTAL_CODE_MAX_LENGTH))
const AddressCountryCodeInputSchema = z
	.string()
	.trim()
	.apply(utf16Length("exact", 2))
	.overwrite((value) => value.toUpperCase())
const US_ADDRESS_REGION_CODES = [...US_STATE_CODES, "DC"] as const
const UsAddressRegionSchema = z.enum(US_ADDRESS_REGION_CODES)

function addUsAddressIssues(
	value: {
		readonly countryCode?: string | undefined
		readonly region?: string | undefined
		readonly postalCode?: string | undefined
	},
	context: z.core.$RefinementCtx<unknown>,
): void {
	if (value.countryCode !== "US") return
	if (value.region !== undefined && !UsAddressRegionSchema.validate(value.region)) {
		context.addIssue({
			code: "custom",
			path: ["region"],
			message: "Select a valid US state or DC.",
		})
	}
	if (value.postalCode !== undefined && !US_ZIP_CODE_PATTERN.test(value.postalCode)) {
		context.addIssue({
			code: "custom",
			path: ["postalCode"],
			message: "Use a valid ZIP or ZIP+4 code.",
		})
	}
}

export const AddressTypeSchema = z.enum(["billing", "shipping"]).meta({ id: "AddressType" })

export type AddressType = z.infer<typeof AddressTypeSchema>

export const AddressSchema = z
	.object({
		id: z.uuid(),
		type: AddressTypeSchema,
		street1: z.string(),
		street2: z.string().optional(),
		city: z.string(),
		region: z.string(),
		postalCode: z.string(),
		countryCode: z.string(),
		isDefault: z.boolean(),
	})
	.meta({ id: "Address" })

export type Address = z.infer<typeof AddressSchema>

export const PatientAddressesResponseSchema = z
	.object({
		addresses: z.array(AddressSchema),
		recipientFirstName: z.string().optional(),
		recipientLastName: z.string().optional(),
	})
	.meta({ id: "PatientAddressesResponse" })

export type PatientAddressesResponse = z.infer<typeof PatientAddressesResponseSchema>

export const PatientSchema = z
	.object({
		id: z.uuid(),
		userId: z.uuid(),
		universalPatientId: z.uuid(),
		firstName: z.string().optional(),
		lastName: z.string().optional(),
		dateOfBirth: z.string().optional(),
		state: z.string().optional(),
		consentToTelehealth: z.boolean().optional(),
		gender: z.string().optional(),
		genderIdentity: z.string().optional(),
		email: z.string().optional(),
		phone: z.string().optional(),
		orderCount: z.number().int(),
		deactivatedAt: IsoTimestampSchema.nullable(),
		createdAt: IsoTimestampSchema,
	})
	.meta({ id: "Patient" })

export type Patient = z.infer<typeof PatientSchema>

export const UpdatePatientProfileSchema = z
	.object({
		firstName: createRequiredPersonNameSchema("First name").optional(),
		lastName: createRequiredPersonNameSchema("Last name").optional(),
		dateOfBirth: z.iso.date().optional(),
		state: z.string().min(1).optional(),
		consentToTelehealth: z.boolean().optional(),
		gender: z.preprocess(
			(value) => {
				if (value === null || value === undefined) {
					return undefined
				}
				if (typeof value !== "string") {
					return value
				}
				const trimmed = value.trim()
				return trimmed.length === 0 ? undefined : trimmed
			},
			z.string().min(1).optional(),
		),
		genderIdentity: z.preprocess(
			(value) => {
				if (value === null || value === undefined) {
					return undefined
				}
				if (typeof value !== "string") {
					return value
				}
				const trimmed = value.trim()
				return trimmed.length === 0 ? undefined : trimmed
			},
			z.string().min(1).optional(),
		),
	})
	.meta({ id: "UpdatePatientProfile" })

export type UpdatePatientProfile = z.infer<typeof UpdatePatientProfileSchema>

export const UpdatePatientAddressSchema = z
	.object({
		street1: RequiredAddressStreetSchema.optional(),
		street2: OptionalAddressStreetSchema.nullable().optional(),
		city: AddressCityInputSchema.optional(),
		region: AddressRegionInputSchema.optional(),
		postalCode: AddressPostalCodeInputSchema.optional(),
		countryCode: AddressCountryCodeInputSchema.optional(),
		isDefault: z.boolean().optional(),
	})
	.overwrite((value) =>
		value.countryCode === "US" && value.region !== undefined
			? { ...value, region: value.region.toUpperCase() }
			: value,
	)
	.superRefine((value, context) => addUsAddressIssues(value, context))
	.meta({ id: "UpdatePatientAddress" })

export type UpdatePatientAddress = z.infer<typeof UpdatePatientAddressSchema>

export const CreateAddressSchema = z
	.object({
		type: AddressTypeSchema,
		street1: RequiredAddressStreetSchema,
		street2: OptionalAddressStreetSchema.nullable().optional(),
		city: AddressCityInputSchema,
		region: AddressRegionInputSchema,
		postalCode: AddressPostalCodeInputSchema,
		countryCode: AddressCountryCodeInputSchema,
		isDefault: z.boolean().default(false),
	})
	.overwrite((value) =>
		value.countryCode === "US"
			? { ...value, region: value.region.toUpperCase() }
			: value,
	)
	.superRefine((value, context) => addUsAddressIssues(value, context))
	.meta({ id: "CreateAddress" })

export type CreateAddress = z.infer<typeof CreateAddressSchema>

export const PatientFiltersSchema = z
	.object({
		status: z.enum(["active", "deactivated"]).optional(),
		search: z.string().trim().apply(utf16Length("max", PATIENT_SEARCH_MAX_LENGTH)).optional(),
		page: z.coerce.number().int().positive().default(1),
		limit: z.coerce.number().int().positive().max(100).default(25),
	})
	.meta({ id: "PatientFilters" })

export type PatientFilters = z.infer<typeof PatientFiltersSchema>
